import {
    init,
    reactErrorHandler,
    reactRouterBrowserTracingIntegration,
    setUser,
    captureMessage,
} from '@sentry/react';
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';
import { AuthenticatedUser } from '~/auth/user.ts';
import { isLocal } from '~/utils/env.ts';

export function setupMonitoring() {
    if (isLocal()) return;
    init({
        dsn: 'https://da14778fc6ddb154e35f150791ea577e@o4510436824186880.ingest.us.sentry.io/4510436863311872',
        release: getReleaseName(),
        environment: import.meta.env.VITE_APP_ENVIRONMENT || 'local',
        sendDefaultPii: false,
        integrations: [
            reactRouterBrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        tracesSampleRate: 0.25,
        denyUrls: [
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i,
            /li\.lms-analytics/i,
            /px\.ads\.linkedin\.com/i,
            /snap\.licdn\.com/i,
        ],
        ignoreErrors: [/window\.ethereum/],
    });
}

export const errorHandlers = {
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: reactErrorHandler(),
};

export function setupMonitoringUser(user: AuthenticatedUser) {
    setUser({ id: user.id, email: user.email });
}

export function reportError(
    msg: string,
    level: 'fatal' | 'error' | 'warning' | 'log' | 'debug' | 'info',
) {
    captureMessage(msg, level);
}

function getReleaseName() {
    if (import.meta.env.VITE_APP_ENVIRONMENT === 'staging') {
        return `platform@${import.meta.env.VITE_APP_VERSION || 'unknown'}`;
    }
    const version = (import.meta.env.VITE_APP_VERSION || 'unknown').replace('v', '');
    return `platform@${version}`;
}
